import React, { useEffect, useState } from 'react';
import apiClient, { formatApiError } from '../utils/api';
import { toast } from 'sonner';
import * as PhosphorIcons from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const { FolderOpen, Plus, Trash, Broadcast, Pencil } = PhosphorIcons;

const Groups = () => {
  const [groups, setGroups] = useState([]);
  const [stores, setStores] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const [pushOpen, setPushOpen] = useState(false);
  const [pushGroup, setPushGroup] = useState(null);
  const [pushPlaylistId, setPushPlaylistId] = useState('');
  const [pushing, setPushing] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '' });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [g, s, p] = await Promise.all([
        apiClient.get('/store-groups'), apiClient.get('/stores'), apiClient.get('/playlists')
      ]);
      setGroups(g.data); setStores(s.data); setPlaylists(p.data);
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) { toast.error('Group name required'); return; }
    try {
      await apiClient.post('/store-groups', formData);
      toast.success('Group created');
      setCreateOpen(false);
      setFormData({ name: '', description: '' });
      fetchData();
    } catch (error) { toast.error(formatApiError(error)); }
  };

  const handleEdit = async () => {
    if (!formData.name.trim()) { toast.error('Group name required'); return; }
    try {
      await apiClient.put(`/store-groups/${editGroup.id}`, formData);
      toast.success('Group updated');
      setEditOpen(false);
      setEditGroup(null);
      setFormData({ name: '', description: '' });
      fetchData();
    } catch (error) { toast.error(formatApiError(error)); }
  };

  const openEdit = (group) => {
    setEditGroup(group);
    setFormData({ name: group.name, description: group.description || '' });
    setEditOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this group? Stores will be unassigned from this group.')) return;
    try {
      await apiClient.delete(`/store-groups/${id}`);
      toast.success('Group deleted');
      fetchData();
    } catch (error) { toast.error(formatApiError(error)); }
  };

  const openPushDialog = (group) => {
    setPushGroup(group);
    setPushPlaylistId('');
    setPushOpen(true);
  };

  const handlePushPlaylist = async () => {
    if (!pushPlaylistId || !pushGroup) return;
    setPushing(true);
    try {
      const res = await apiClient.post(`/store-groups/${pushGroup.id}/push-playlist`, { playlist_id: pushPlaylistId });
      toast.success(res.data.message);
      setPushOpen(false);
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setPushing(false); }
  };

  const getGroupStoreCount = (groupId) => stores.filter(s => s.group_id === groupId).length;

  if (loading) return <div className="text-[#6b665c] font-mono">Loading groups...</div>;

  return (
    <div className="space-y-6" data-testid="groups-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2" data-testid="groups-title">Store Groups</h1>
          <p className="text-[#6b665c] font-mono text-sm tracking-wider uppercase">Organize &amp; Broadcast</p>
        </div>

        <Dialog open={createOpen} onOpenChange={(v) => { setCreateOpen(v); if (!v) setFormData({ name: '', description: '' }); }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white border-0" data-testid="create-group-btn">
              <Plus size={18} className="mr-2" />New Group
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border border-[#c5d4c8] text-white">
            <DialogHeader><DialogTitle className="font-mono">Create Store Group</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Group Name</Label>
                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="e.g. North Region" className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]" data-testid="group-name-input" />
              </div>
              <div>
                <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Description</Label>
                <Input value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="Optional description" className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]" data-testid="group-desc-input" />
              </div>
              <Button onClick={handleCreate} className="w-full bg-blue-600 hover:bg-blue-700 text-white border-0" data-testid="save-group-btn">Create Group</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={(v) => { setEditOpen(v); if (!v) { setEditGroup(null); setFormData({ name: '', description: '' }); } }}>
        <DialogContent className="bg-white border border-[#c5d4c8] text-white">
          <DialogHeader><DialogTitle className="font-mono">Edit Group</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Group Name</Label>
              <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]" data-testid="edit-group-name-input" />
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Description</Label>
              <Input value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]" data-testid="edit-group-desc-input" />
            </div>
            <Button onClick={handleEdit} className="w-full bg-blue-600 hover:bg-blue-700 text-white border-0" data-testid="update-group-btn">Update Group</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Push Playlist Dialog */}
      <Dialog open={pushOpen} onOpenChange={setPushOpen}>
        <DialogContent className="bg-white border border-[#c5d4c8] text-white">
          <DialogHeader><DialogTitle className="font-mono flex items-center gap-2"><Broadcast size={20} className="text-green-400" />Push Playlist to "{pushGroup?.name}"</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#6b665c]">
              Set the active playlist for all <strong>{pushGroup ? getGroupStoreCount(pushGroup.id) : 0}</strong> store(s) in this group.
            </p>
            <div>
              <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Playlist</Label>
              <Select value={pushPlaylistId} onValueChange={setPushPlaylistId}>
                <SelectTrigger className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]" data-testid="push-playlist-select"><SelectValue placeholder="Choose a playlist..." /></SelectTrigger>
                <SelectContent className="bg-white border-[#c5d4c8] text-[#2d2a26]">
                  {playlists.map(pl => <SelectItem key={pl.id} value={pl.id}>{pl.name} ({(pl.track_ids || []).length} tracks)</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handlePushPlaylist} disabled={!pushPlaylistId || pushing} className="w-full bg-green-600 hover:bg-green-700 text-white border-0" data-testid="confirm-push-btn">
              <Broadcast size={16} className="mr-2" />{pushing ? 'Pushing...' : 'Push Playlist Now'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Groups List */}
      {groups.length === 0 ? (
        <div className="bg-white border border-[#c5d4c8] rounded-md p-12 text-center">
          <FolderOpen size={48} className="text-[#a39e92] mx-auto mb-4" />
          <p className="text-[#8a8477] font-mono">No groups yet. Create one to organize your stores.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.map(group => {
            const storeCount = getGroupStoreCount(group.id);
            const groupStores = stores.filter(s => s.group_id === group.id);
            return (
              <div key={group.id} className="bg-white border border-[#c5d4c8] rounded-md p-5 hover:border-[#c5d4c8] transition-all" data-testid={`group-card-${group.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md bg-[#e4ebe5] border border-[#c5d4c8] flex items-center justify-center">
                      <FolderOpen size={20} className="text-[#7c9082]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-sm">{group.name}</h3>
                      <p className="text-xs text-[#8a8477] font-mono">{storeCount} store{storeCount !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(group)} className="text-[#8a8477] hover:text-[#7c9082] transition-colors p-1" data-testid={`edit-group-${group.id}`}>
                      <Pencil size={16} />
                    </button>
                    <button onClick={() => handleDelete(group.id)} className="text-[#8a8477] hover:text-red-500 transition-colors p-1" data-testid={`delete-group-${group.id}`}>
                      <Trash size={16} />
                    </button>
                  </div>
                </div>

                {group.description && <p className="text-xs text-[#8a8477] mb-3">{group.description}</p>}

                {groupStores.length > 0 && (
                  <div className="space-y-1 mb-4">
                    {groupStores.slice(0, 4).map(s => (
                      <div key={s.id} className="flex items-center gap-2 text-xs">
                        <div className={`w-1.5 h-1.5 rounded-full ${s.is_online ? 'bg-green-500' : 'bg-zinc-600'}`} />
                        <span className="text-[#6b665c] truncate">{s.name}</span>
                      </div>
                    ))}
                    {groupStores.length > 4 && <p className="text-xs text-[#8a8477] font-mono">+{groupStores.length - 4} more</p>}
                  </div>
                )}

                <Button onClick={() => openPushDialog(group)} disabled={storeCount === 0 || playlists.length === 0}
                  className="w-full bg-green-600/20 hover:bg-green-600/30 text-green-400 border border-green-600/30 h-8 text-xs font-mono"
                  data-testid={`push-playlist-${group.id}`}>
                  <Broadcast size={14} className="mr-1.5" />Push Playlist
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Groups;
